import React, { useMemo } from 'react';
import DashboardCard from '../shared/DashboardCard';
import styles from '../../../../styles/DashboardV2.module.css';
import { organizeDocumentsByPeriod, isDocumentExpired } from '../../../../utils/documentUtils';
import type { Document, DocumentType } from '../../../../types/Document';

interface AgentData {
    documents?: Document[];
}

interface ComplianceRiskForecastWidgetProps {
    agencyUsers: AgentData[];
    documentTypes: DocumentType[];
    loading?: boolean;
}

const ComplianceRiskForecastWidget: React.FC<ComplianceRiskForecastWidgetProps> = ({
    agencyUsers,
    documentTypes,
    loading,
}) => {
    const forecast = useMemo(() => {
        const now = new Date();
        const in30  = new Date(now.getTime() + 30  * 86400000);
        const in60  = new Date(now.getTime() + 60  * 86400000);
        const in90  = new Date(now.getTime() + 90  * 86400000);

        let critical = 0;   // ≤30 days
        let moderate = 0;   // 31–60 days
        let upcoming = 0;   // 61–90 days

        (agencyUsers ?? []).forEach(user => {
            const userDocs = user.documents ?? [];

            (documentTypes ?? []).forEach(docType => {
                const typeDocuments = userDocs.filter(doc => doc.typeID === docType.id);
                if (typeDocuments.length === 0) return;

                // Resolve down to the single operative document for this type/period —
                // the same resolution calculateDocumentTypeCompletion uses — so an
                // already-renewed document doesn't also count as still at risk.
                const { currentDocument } = organizeDocumentsByPeriod(typeDocuments, docType);
                if (!currentDocument || !currentDocument.expiryDate) return;
                if (isDocumentExpired(currentDocument)) return; // already expired, not a forecast risk

                const exp = new Date(currentDocument.expiryDate as any);
                if (exp <= now) return;
                if (exp <= in30) critical++;
                else if (exp <= in60) moderate++;
                else if (exp <= in90) upcoming++;
            });
        });

        return { critical, moderate, upcoming, total: critical + moderate + upcoming };
    }, [agencyUsers, documentTypes]);

    const rows = [
        { label: 'Next 30 Days',  sub: 'Critical',  count: forecast.critical, color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
        { label: 'Next 31–60 Days', sub: 'Moderate', count: forecast.moderate, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
        { label: 'Next 61–90 Days', sub: 'Upcoming', count: forecast.upcoming, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
    ];

    return (
        <DashboardCard
            title="Compliance Risk Forecast"
            loading={loading}
        >
            <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: '2rem', fontWeight: 700, color: '#0f172a' }}>{forecast.total}</span>
                <span style={{ fontSize: '0.85rem', color: '#64748b', marginLeft: 8 }}>documents expiring in 90 days</span>
            </div>
            <div className={styles.forecastList}>
                {rows.map(row => (
                    <div
                        key={row.label}
                        className={styles.forecastItem}
                        style={{ background: row.bg, borderColor: row.border }}
                    >
                        <div className={styles.forecastItemLeft}>
                            <span className={styles.forecastItemLabel}>{row.label}</span>
                            <span className={styles.forecastItemSub} style={{ color: row.color }}>{row.sub}</span>
                        </div>
                        <span className={styles.forecastItemCount} style={{ color: row.color }}>
                            {row.count}
                        </span>
                    </div>
                ))}
            </div>
        </DashboardCard>
    );
};

export default ComplianceRiskForecastWidget;
